import React, { useEffect, useState } from "react";
import { Card, message, Tabs, Input, Button, Row, Col, Space, Spin, Modal, Tooltip } from "antd";
import { ArrowLeftOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { Link, useParams } from "react-router-dom";
import { handleError } from "../../common/ErrorHandling";
import { BreadcrumbsBar } from "../../common/components/BreadcrumbsBar";
import { convertGoogleDoc, getReport, upsertReport } from "../../common/api/ReportClient";
import { POCReport } from "../../types/ReportTypes";
import { getOrderForm } from "../../common/api/OrderFormClient";
import { OrderForm } from "../../types/OrderFormTypes";
import { html as beautifyHtml } from "js-beautify";

const { TextArea } = Input;

enum SectionKey {
    ContractSummary = "contractSummary",
    NegotiationStrategy = "negotiationStrategy",
    TopContractualPriorities = "topContractualPriorities",
}

const sections = [
    {
        sectionKey: SectionKey.ContractSummary,
        title: "Contract summary",
    },
    {
        sectionKey: SectionKey.NegotiationStrategy,
        title: "Negotiation strategy",
    },
    {
        sectionKey: SectionKey.TopContractualPriorities,
        title: "Top contractual priorities",
    },
];

type POCReportContent = Pick<
    POCReport,
    "contractSummary" | "negotiationStrategy" | "topLegalPriorities"
>;

const contentFromReport = (report: POCReportContent | undefined): POCReportContent => {
    return {
        contractSummary: report?.contractSummary ?? "",
        negotiationStrategy: report?.negotiationStrategy ?? "",
        topLegalPriorities: report?.topLegalPriorities ?? "",
    };
};

const getPOCReportSection = (content: POCReportContent, sectionKey: SectionKey): string => {
    switch (sectionKey) {
        case SectionKey.ContractSummary:
            return content.contractSummary;
        case SectionKey.NegotiationStrategy:
            return content.negotiationStrategy;
        case SectionKey.TopContractualPriorities:
            return content.topLegalPriorities;
    }
};

const setPOCReportSection = (
    prevContent: POCReportContent,
    sectionKey: SectionKey,
    sectionContent: string
) => {
    const newContent = { ...prevContent };
    switch (sectionKey) {
        case SectionKey.ContractSummary:
            newContent.contractSummary = sectionContent;
            break;
        case SectionKey.NegotiationStrategy:
            newContent.negotiationStrategy = sectionContent;
            break;
        case SectionKey.TopContractualPriorities:
            newContent.topLegalPriorities = sectionContent;
            break;
    }
    return newContent;
};

/**
 * Format the HTML
 *
 * @param html The HTML string to format
 */
export const formatHtml = (html: string): string => {
    return beautifyHtml(html, {
        indent_size: 2,
        preserve_newlines: true,
        content_unformatted: ["pre", "code", "textarea"],
    });
};

/**
 * Compress the HTML by removing extra whitespace
 *
 * @param html The HTML string to compress
 */
export const compressHtml = (html: string): string => {
    try {
        // Remove extra whitespace, newlines, and tabs
        return html.replace(/\s+/g, " ").replace(/>\s+</g, "><").trim();
    } catch (error) {
        console.error("Error compressing HTML:", error);
        return html;
    }
};

export const OrderFormSummary = (props: {}) => {
    const params = useParams();
    const [orderForm, setOrderForm] = useState({} as OrderForm);
    const [report, setReport] = useState({} as POCReport);
    const [sectionKey, setSectionKey] = useState(sections[0].sectionKey);
    const [_content, _setContent] = useState(contentFromReport({} as POCReport));
    const [fileIdOrUrl, setFileIdOrUrl] = useState("");
    const [isConverting, setIsConverting] = useState(false);
    const [showFullReport, setShowFullReport] = useState(false);
    const [showHelpModal, setShowHelpModal] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const orderFormId = Number(params.orderFormId) || 0;

    const loadOrder = async () => {
        if (!orderFormId) return;
        try {
            const data = await getOrderForm(orderFormId);
            setOrderForm(data.orderForm);
            return data.orderForm;
        } catch (error) {
            handleError(error);
        }
    };

    const loadReport = async () => {
        if (!orderFormId) return;
        try {
            const data = await getReport(orderFormId);
            setReport(data);
            return data;
        } catch (error) {
            handleError(error);
        }
    };

    const loadData = async () => {
        setIsLoadingData(true);
        try {
            const [, newReport] = await Promise.all([loadOrder(), loadReport()]);
            _setContent(contentFromReport(newReport));
        } catch (error) {
            handleError(error);
        } finally {
            setIsLoadingData(false);
        }
    };

    const getSectionContent = (sectionKey: SectionKey): string => {
        return getPOCReportSection(_content, sectionKey);
    };

    const isSectionDirty = (sectionKey: SectionKey): boolean => {
        const originalSectionContent = getPOCReportSection(contentFromReport(report), sectionKey);
        const currentSectionContent = getSectionContent(sectionKey);
        return originalSectionContent !== currentSectionContent;
    };

    const anySectionDirty = (): boolean => {
        return sections.some(({ sectionKey }) => isSectionDirty(sectionKey));
    };

    const setSectionContent = (sectionKey: SectionKey, sectionContent: string) => {
        _setContent((prevContent) => setPOCReportSection(prevContent, sectionKey, sectionContent));
    };

    const updateSectionContent = (sectionKey: SectionKey, fn: (s: string) => string) => {
        _setContent((prevContent) => {
            const prevSectionContent = getPOCReportSection(prevContent, sectionKey);
            const newSectionContent = fn(prevSectionContent);
            return setPOCReportSection(prevContent, sectionKey, newSectionContent);
        });
    };

    const handleReportChange = async (sectionKey: SectionKey) => {
        if (!orderFormId) return;
        const sectionContent = getSectionContent(sectionKey);
        const toUpdate = setPOCReportSection(contentFromReport(report), sectionKey, sectionContent);
        await upsertReport({ ...report, ...toUpdate, orderFormId });
        const newReport = await loadReport();
        const newSectionContent = getPOCReportSection(contentFromReport(newReport), sectionKey);
        setSectionContent(sectionKey, newSectionContent);
        message.success("Successfully saved");
    };

    const handleConvert = async () => {
        if (!fileIdOrUrl.trim()) {
            message.warning("Please enter a Google Doc URL or File ID");
            return;
        }

        setIsConverting(true);
        try {
            const result = (await convertGoogleDoc(fileIdOrUrl.trim())) as {
                success: boolean;
                data?: {
                    fileId: string;
                    sections: Array<{
                        id: number;
                        title: string;
                        content: string;
                    }>;
                };
                error?: string;
            };

            if (result.success && result.data) {
                const sections = result.data.sections ?? [];
                const updatedContent = sections.reduce((updatedContent, section) => {
                    switch (section.id) {
                        case 1:
                            return setPOCReportSection(
                                updatedContent,
                                SectionKey.ContractSummary,
                                section.content
                            );
                        case 2:
                            return setPOCReportSection(
                                updatedContent,
                                SectionKey.NegotiationStrategy,
                                section.content
                            );
                        case 3:
                            return setPOCReportSection(
                                updatedContent,
                                SectionKey.TopContractualPriorities,
                                section.content
                            );
                        default:
                            return updatedContent;
                    }
                }, contentFromReport(undefined));
                _setContent(updatedContent);
                message.success("Document converted successfully");
            } else {
                message.error(result.error || "Failed to convert document");
            }
        } catch (error) {
            console.error("Error converting document:", error);
            handleError(error);
        } finally {
            setIsConverting(false);
        }
    };

    const handleRevertAll = () => {
        setFileIdOrUrl("");
        _setContent(contentFromReport(report));
        message.success("All sections reverted successfully");
    };

    const handleFullReport = () => {
        setShowFullReport(true);
    };

    const handleSaveAll = async () => {
        if (!orderFormId) {
            message.error("Invalid order form ID");
            return;
        }

        setIsSaving(true);
        try {
            // Save all three sections
            await upsertReport({ ...report, ..._content, orderFormId });
            const newReport = await loadReport();
            _setContent(contentFromReport(newReport));
            message.success("All sections saved successfully");
        } catch (error) {
            console.error("Error saving all sections:", error);
            handleError(error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleShowHelp = () => {
        setShowHelpModal(true);
    };

    const handleFormat = () => {
        try {
            updateSectionContent(sectionKey, formatHtml);
            message.success("HTML formatted successfully");
        } catch (error) {
            console.error("Error formatting HTML:", error);
            message.error("Failed to format HTML");
        }
    };

    const handleCompress = () => {
        try {
            updateSectionContent(sectionKey, compressHtml);
            message.success("HTML compressed successfully");
        } catch (error) {
            console.error("Error compressing HTML:", error);
            message.error("Failed to compress HTML");
        }
    };

    const handlePreview = () => {
        setShowPreviewModal(true);
    };

    const handleRevert = () => {
        const originalSectionContent = getPOCReportSection(contentFromReport(report), sectionKey);
        setSectionContent(sectionKey, originalSectionContent);
        message.success("Reverted successfully");
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await handleReportChange(sectionKey);
        } catch (error) {
            console.error("Error saving content:", error);
            message.error("Failed to save");
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [orderFormId]);

    const tabItems = sections.map(({ sectionKey, title }) => {
        const content = getSectionContent(sectionKey);
        const isDirty = isSectionDirty(sectionKey);
        return {
            key: sectionKey,
            label: title,
            children: (
                <div>
                    {(isConverting || isLoadingData) && (
                        <div style={{ textAlign: "center", padding: "20px" }}>
                            <Spin size="large" />
                            <p style={{ marginTop: "10px" }}>
                                {isConverting ? "Converting document..." : "Loading data..."}
                            </p>
                        </div>
                    )}
                    {!isConverting && !isLoadingData && (
                        <>
                            <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                                <Col>
                                    <Space>
                                        <Button
                                            onClick={handleFormat}
                                            disabled={isSaving || !content}
                                        >
                                            Format
                                        </Button>
                                        <Button
                                            onClick={handleCompress}
                                            disabled={isSaving || !content}
                                        >
                                            Compress
                                        </Button>
                                    </Space>
                                </Col>
                                <Col>
                                    <Space>
                                        <Button onClick={handlePreview}>Preview</Button>
                                        <Button
                                            danger
                                            onClick={handleRevert}
                                            disabled={isSaving || !isDirty}
                                        >
                                            Revert
                                        </Button>
                                        <Button
                                            type="primary"
                                            onClick={handleSave}
                                            loading={isSaving}
                                            disabled={!isDirty}
                                        >
                                            Save
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                            <TextArea
                                value={content}
                                onChange={(e) => setSectionContent(sectionKey, e.target.value)}
                                autoSize={{ minRows: 20, maxRows: 40 }}
                                style={{
                                    fontFamily: 'Monaco, Menlo, "Courier New", monospace',
                                    fontSize: "12px",
                                    lineHeight: "1.5",
                                }}
                            />
                        </>
                    )}
                </div>
            ),
        };
    });

    return (
        <div>
            <Card bordered={false} bodyStyle={{ paddingTop: 10 }}>
                <Row>
                    <Col span={24}>
                        <BreadcrumbsBar
                            breadcrumbs={[
                                {
                                    name: "Contracts",
                                    path: "/order/new",
                                },
                                {
                                    name: `${orderForm.name || `Order form ${orderFormId}`}`,
                                    path:
                                        orderForm.id == null ? undefined : `/order/${orderForm.id}`,
                                },
                            ]}
                        />
                        {orderForm.id != null && (
                            <Link to={`/order/${orderForm.id}`}>
                                <Space>
                                    <ArrowLeftOutlined />
                                    Back to Report
                                </Space>
                            </Link>
                        )}
                    </Col>
                </Row>
                <Row gutter={8} align="middle" style={{ marginTop: 16 }}>
                    <Col span={12}>
                        <Input
                            placeholder="Enter Google Doc URL or File ID"
                            value={fileIdOrUrl}
                            onChange={(e) => setFileIdOrUrl(e.target.value)}
                            onPressEnter={handleConvert}
                            style={{ width: "100%" }}
                            disabled={isConverting}
                        />
                    </Col>
                    <Col span={12}>
                        <Row justify="space-between" align="middle">
                            <Col>
                                <Space>
                                    <Button
                                        type="primary"
                                        onClick={handleConvert}
                                        loading={isConverting}
                                        disabled={!fileIdOrUrl.trim()}
                                    >
                                        {isConverting ? "Converting..." : "Convert"}
                                    </Button>
                                </Space>
                            </Col>
                            <Col>
                                <Space>
                                    <Button type="default" onClick={handleFullReport}>
                                        Preview Full Report
                                    </Button>
                                    <Button
                                        danger
                                        onClick={handleRevertAll}
                                        disabled={
                                            isConverting ||
                                            isLoadingData ||
                                            isSaving ||
                                            !anySectionDirty()
                                        }
                                    >
                                        Revert All
                                    </Button>
                                    <Button
                                        type="primary"
                                        onClick={handleSaveAll}
                                        loading={isSaving}
                                        disabled={
                                            isConverting || isLoadingData || !anySectionDirty()
                                        }
                                    >
                                        Save All
                                    </Button>
                                    <Tooltip title="Usage Guide">
                                        <Button
                                            type="text"
                                            icon={<QuestionCircleOutlined />}
                                            onClick={handleShowHelp}
                                            style={{ color: "#1890ff" }}
                                        />
                                    </Tooltip>
                                </Space>
                            </Col>
                        </Row>
                    </Col>
                </Row>
                <Tabs
                    activeKey={sectionKey}
                    items={tabItems}
                    onChange={(key) => setSectionKey(key as SectionKey)}
                />
            </Card>

            {/* Full Report Modal */}
            <Modal
                title="Full Report"
                open={showFullReport}
                onCancel={() => setShowFullReport(false)}
                width="80%"
                style={{ top: 20 }}
                footer={[
                    <Button key="close" onClick={() => setShowFullReport(false)}>
                        Close
                    </Button>,
                ]}
            >
                <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    {sections.map(({ sectionKey, title }) => (
                        <Card
                            key={sectionKey}
                            title={title}
                            style={{ marginBottom: 16 }}
                            size="small"
                        >
                            <div className={`il-summary-wrapper il-summary-wrapper--${sectionKey}`}>
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: getSectionContent(sectionKey),
                                    }}
                                    className="il-summary il-summary-editor"
                                />
                            </div>
                        </Card>
                    ))}
                </div>
            </Modal>

            {/* Help Modal */}
            <Modal
                title="Google Doc Converter Usage Guide"
                open={showHelpModal}
                onCancel={() => setShowHelpModal(false)}
                width="70%"
                style={{ top: 20 }}
                footer={[
                    <Button key="close" onClick={() => setShowHelpModal(false)}>
                        Close
                    </Button>,
                ]}
            >
                <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    <div style={{ marginBottom: 24 }}>
                        <h3>Google Cloud Setup</h3>
                        <ol>
                            <li>
                                Go to{" "}
                                <a
                                    href="https://console.cloud.google.com/"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Google Cloud Console
                                </a>
                            </li>
                            <li>Create a new project or select an existing one</li>
                            <li>
                                Enable the Google Drive API:
                                <ul>
                                    <li>Go to "APIs & Services" &gt; "Library"</li>
                                    <li>Search for "Google Drive API"</li>
                                    <li>Click "Enable"</li>
                                </ul>
                            </li>
                            <li>
                                Create a Service Account:
                                <ul>
                                    <li>Go to "IAM & Admin" &gt; "Service Accounts"</li>
                                    <li>Click "Create Service Account"</li>
                                    <li>Give it a name and description</li>
                                    <li>Grant "Editor" role</li>
                                    <li>Create and download the JSON key file</li>
                                </ul>
                            </li>
                            <li>
                                Configure Credentials:
                                <ul>
                                    <li>
                                        Create a <code>config</code> directory in the project root
                                    </li>
                                    <li>
                                        Place your downloaded service account JSON file as{" "}
                                        <code>config/credentials.json</code>
                                    </li>
                                </ul>
                            </li>
                        </ol>

                        <h3>Set File Permissions</h3>
                        <div>
                            <strong>Important:</strong> Ensure the service account has access to the
                            target Google Doc or folder:
                        </div>
                        <ol>
                            <li>Open the Google Doc or folder you want to convert</li>
                            <li>Click the "Share" button</li>
                            <li>
                                Add the service account email (Just for test:{" "}
                                <code>doc-converter@oasis-gcp.iam.gserviceaccount.com</code>)
                            </li>
                            <li>Grant "Viewer" permissions</li>
                        </ol>

                        <h3>How to use the converter</h3>
                        <ol>
                            <li>Enter either the Google Doc URL or File ID in the input field</li>
                            <li>
                                Supported formats:
                                <ul>
                                    <li>
                                        Full URL: https://docs.google.com/document/d/FILE_ID/edit
                                    </li>
                                    <li>Direct File ID: 1ABC123xyz...</li>
                                </ul>
                            </li>
                            <li>Click the "Convert" button to start the conversion process</li>
                            <li>Wait for the conversion to complete</li>
                            <li>The converted HTML sections will be displayed in tabs</li>
                            <li>
                                Use the "Format" and "Compress" buttons to format or compress the
                                HTML
                            </li>
                        </ol>
                    </div>
                </div>
            </Modal>

            {/* Preview Modal */}
            <Modal
                title="HTML Preview"
                open={showPreviewModal}
                onCancel={() => setShowPreviewModal(false)}
                width="80%"
                style={{ top: 20 }}
                footer={[
                    <Button key="close" onClick={() => setShowPreviewModal(false)}>
                        Close
                    </Button>,
                ]}
            >
                <div
                    style={{ maxHeight: "70vh", overflowY: "auto" }}
                    className={`il-summary-wrapper il-summary-wrapper--${sectionKey}`}
                >
                    <div
                        dangerouslySetInnerHTML={{ __html: getSectionContent(sectionKey) }}
                        style={{
                            padding: "20px",
                            backgroundColor: "#fff",
                            border: "1px solid #e8e8e8",
                            borderRadius: "4px",
                        }}
                        className="il-summary il-summary-editor"
                    />
                </div>
            </Modal>
        </div>
    );
};
